import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { pwaService } from '../services/pwaService';
import './PWAInstallButton.css';

interface PWAInstallButtonProps {
  className?: string;
  onInstall?: () => void;
  variant?: 'header' | 'banner' | 'floating' | 'auto';
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  className = '', 
  onInstall,
  variant = 'auto'
}) => {
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isNeverShowAgain, setIsNeverShowAgain] = useState(false);

  useEffect(() => {

    // Check if install prompt is available
    setShowInstallButton(pwaService.isInstallPromptAvailable());

    // Check if user has dismissed the banner
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }

    // Check if user has opted to never show again
    const neverShow = localStorage.getItem('pwa-install-never-show');
    if (neverShow === 'true') {
      setIsNeverShowAgain(true);
    }

    // Listen for install events
    const handleInstallAvailable = () => {
      setShowInstallButton(true);
    };

    const handleInstallCompleted = () => {
      setShowInstallButton(false);
      setIsInstalling(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-install-completed', handleInstallCompleted);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-install-completed', handleInstallCompleted);
    };
  }, []);

  const handleInstall = async () => {
    if (isInstalling) return;
    
    setIsInstalling(true);
    
    try {
      const installed = await pwaService.installApp();
      if (installed) {
        onInstall?.();
      }
    } catch (error) {
      console.error('Failed to install PWA:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  const handleNeverShowAgain = () => {
    setIsNeverShowAgain(true);
    setIsDismissed(true);
    localStorage.setItem('pwa-install-never-show', 'true');
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showInstallButton || pwaService.isAppInstalled()) {
    return null;
  }

  // Auto variant: always show banner for both mobile and desktop
  const effectiveVariant = variant === 'auto' ? 'banner' : variant;

  // Don't show banner if dismissed or never show again is set
  if (effectiveVariant === 'banner' && (isDismissed || isNeverShowAgain)) {
    return null;
  }

  // Header variant (compact)
  if (effectiveVariant === 'header') {
    return (
      <button
        className={`pwa-install-button pwa-install-header ${className}`}
        onClick={handleInstall}
        disabled={isInstalling}
        aria-label="Install SymphonyTodo as an app"
      >
        <Download size={16} />
        <span className="pwa-install-text">Install</span>
      </button>
    );
  }

  // Banner variant (mobile-friendly)
  if (effectiveVariant === 'banner') {
    return (
      <div className="pwa-install-banner">
        <div className="pwa-install-banner-content">
          <div className="pwa-install-banner-icon">
            <Smartphone size={20} />
          </div>
          <div className="pwa-install-banner-text">
            <div className="pwa-install-banner-title">Install SymphonyTodo</div>
            <div className="pwa-install-banner-subtitle">Get the full app experience</div>
          </div>
          <div className="pwa-install-banner-actions">
            <button
              className="pwa-install-banner-button"
              onClick={handleInstall}
              disabled={isInstalling}
            >
              {isInstalling ? 'Installing...' : 'Install'}
            </button>
            <button
              className="pwa-install-banner-never-show"
              onClick={handleNeverShowAgain}
              aria-label="Never show install prompt again"
            >
              Never show again
            </button>
            <button
              className="pwa-install-banner-dismiss"
              onClick={handleDismiss}
              aria-label="Dismiss install prompt"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Floating variant (original but smaller)
  return (
    <button
      className={`pwa-install-button pwa-install-floating ${className}`}
      onClick={handleInstall}
      disabled={isInstalling}
      aria-label="Install SymphonyTodo as an app"
    >
      <Download size={20} />
    </button>
  );
};

export default PWAInstallButton;
